// Currency helper functions for CheF
import { deliveryFeeForMiles } from './commission';

/**
 * Format amount as Eastern Caribbean Dollar (XCD)
 * @param {number} amount - The amount to format
 * @returns {string} Formatted XCD amount
 */
export const formatCurrencyXCD = (amount) => {
  return new Intl.NumberFormat('en-XC', {
    style: 'currency',
    currency: 'XCD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

/**
 * Format amount as US Dollar (USD)
 * @param {number} amount - The amount to format
 * @returns {string} Formatted USD amount
 */
export const formatCurrencyUSD = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

/**
 * Convert XCD to USD (approximate rate: 1 XCD = 0.37 USD)
 * @param {number} xcdAmount - Amount in XCD
 * @returns {number} Amount in USD
 */
export const convertXCDtoUSD = (xcdAmount) => {
  const conversionRate = 0.37; // 1 XCD = 0.37 USD (approximate)
  return xcdAmount * conversionRate;
};

/**
 * Convert USD to XCD (approximate rate: 1 USD = 2.70 XCD)
 * @param {number} usdAmount - Amount in USD
 * @returns {number} Amount in XCD
 */
export const convertUSDtoXCD = (usdAmount) => {
  const conversionRate = 2.70; // 1 USD = 2.70 XCD (approximate)
  return usdAmount * conversionRate;
};

/**
 * Get currency symbol for XCD
 * @returns {string} XCD currency symbol
 */
export const getXCDSymbol = () => {
  return '$'; // XCD uses the same symbol as USD
};

/**
 * Get currency symbol for USD
 * @returns {string} USD currency symbol
 */
export const getUSDSymbol = () => {
  return '$';
};

/**
 * Display both XCD and USD amounts
 * @param {number} xcdAmount - Amount in XCD
 * @returns {object} Object with formatted XCD and USD strings
 */
export const displayBothCurrencies = (xcdAmount) => {
  const usdAmount = convertXCDtoUSD(xcdAmount);
  return {
    xcd: formatCurrencyXCD(xcdAmount),
    usd: formatCurrencyUSD(usdAmount),
  };
};

/**
 * Convert amount to cents for Stripe (requires USD)
 * @param {number} xcdAmount - Amount in XCD
 * @returns {number} Amount in cents (USD)
 */
export const convertToCents = (xcdAmount) => {
  const usdAmount = convertXCDtoUSD(xcdAmount);
  return Math.round(usdAmount * 100);
};

/**
 * Convert cents from Stripe to XCD
 * @param {number} cents - Amount in cents (USD)
 * @returns {number} Amount in XCD
 */
export const convertFromCents = (cents) => {
  const usdAmount = cents / 100;
  return convertUSDtoXCD(usdAmount);
};

/**
 * Calculate chef service fee (2%)
 * @param {number} amount - Base amount
 * @returns {number} Chef service fee amount
 */
export const calculateChefServiceFee = (amount) => {
  return amount * 0.02; // 2% Chef service fee
};

/**
 * Calculate delivery fee based on distance or order type
 * @param {object} options - Delivery options
 * @returns {number} Delivery fee in XCD
 */
export const calculateDeliveryFee = (options = {}) => {
  const { orderType, distance, island } = options;
  
  if (orderType === 'pickup') {
    return 0;
  }

  // iOS DeliveryFeeCalculator: $15 covers the first mile, +$3 per extra mile
  return deliveryFeeForMiles(distance);
};

/**
 * Calculate total order amount
 * @param {object} options - Order calculation options
 * @returns {object} Order calculation breakdown
 */
export const calculateOrderTotal = (options = {}) => {
  const { subtotal, orderType, distance, island } = options;
  
  const chefServiceFee = calculateChefServiceFee(subtotal);
  const deliveryFee = calculateDeliveryFee({ orderType, distance, island });
  const total = subtotal + chefServiceFee + deliveryFee;
  
  return {
    subtotal,
    chefServiceFee,
    deliveryFee,
    total,
    totalUSD: convertXCDtoUSD(total),
    totalCents: convertToCents(total),
  };
};
